import { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Collapse,
  Alert,
  IconButton,
  InputAdornment
} from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { couponService, type ValidateCouponResult } from '../../../shared/services/coupon/couponService';
import { mergeTheme } from '../types';
import type { ThemeCustomization } from '../types';
import { trackCouponApplied } from '../../../shared/analytics/gtm';
import type { TrackableEvent } from '../../../shared/analytics/types';

interface CouponFieldProps {
  eventId: string;
  ticketType?: string;
  ticketValue: number;
  onCouponApplied: (result: ValidateCouponResult & { code: string }) => void;
  onCouponRemoved: () => void;
  themeCustomization?: ThemeCustomization;
  trackingEvent?: TrackableEvent;
}

export function CouponField({
  eventId,
  ticketType,
  ticketValue,
  onCouponApplied,
  onCouponRemoved,
  themeCustomization,
  trackingEvent,
}: CouponFieldProps) {
  const mergedTheme = mergeTheme(themeCustomization);
  const primaryColor = mergedTheme.colors?.primaryColor || '#1976D2';
  const [showInput, setShowInput] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ValidateCouponResult | null>(null);
  const [appliedCode, setAppliedCode] = useState<string | null>(null);

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) return;

    setLoading(true);
    try {
      const response = await couponService.validate(
        couponCode.trim(),
        eventId,
        ticketType,
        ticketValue
      );

      setResult(response.data);

      if (response.data.success) {
        setAppliedCode(couponCode.trim().toUpperCase());
        onCouponApplied({ ...response.data, code: couponCode.trim().toUpperCase() });
        if (trackingEvent) {
          trackCouponApplied(
            trackingEvent,
            couponCode.trim().toUpperCase(),
            response.data.finalValue || ticketValue,
          );
        }
      }
    } catch (error: any) {
      setResult({
        success: false,
        message: error?.response?.data?.message || 'Erro ao validar cupom'
      });
    }
    setLoading(false);
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setResult(null);
    setAppliedCode(null);
    onCouponRemoved();
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  // Se já tem cupom aplicado, mostra a versão compacta
  if (appliedCode && result?.success) {
    return (
      <Box
        sx={{
          p: 2,
          bgcolor: 'success.light',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon color="success" />
          <Box>
            <Typography variant="body2" fontWeight={600} color="success.dark" sx={{ fontFamily: 'Manrope, sans-serif' }}>
              Cupom aplicado: {appliedCode}
            </Typography>
            <Typography variant="caption" color="success.dark" sx={{ fontFamily: 'Manrope, sans-serif' }}>
              {result.message}
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={handleRemoveCoupon} color="error">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box>
      {!showInput ? (
        <Button
          variant="text"
          startIcon={<LocalOfferIcon />}
          onClick={() => setShowInput(true)}
          sx={{ textTransform: 'none', fontFamily: 'Manrope, sans-serif' }}
        >
          Tem um cupom de desconto?
        </Button>
      ) : (
        <Collapse in={showInput}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <TextField
              size="medium"
              placeholder="Digite o código do cupom"
              value={couponCode}
              onChange={(e) => {
                setCouponCode(e.target.value.toUpperCase());
                setResult(null); // Limpa resultado anterior
              }}
              disabled={loading}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocalOfferIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => {
                        setShowInput(false);
                        setCouponCode('');
                        setResult(null);
                      }}
                      disabled={loading}
                      sx={{
                        minWidth: 'auto',
                        px: 1,
                        fontFamily: 'Manrope, sans-serif',
                        fontSize: '0.875rem',
                        textTransform: 'none'
                      }}
                    >
                      Cancelar
                    </Button>
                  </InputAdornment>
                ),
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleValidateCoupon();
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontFamily: 'Manrope, sans-serif',
                  fontSize: '1rem',
                  py: 0.5,
                },
                '& .MuiInputLabel-root': {
                  fontFamily: 'Manrope, sans-serif',
                },
              }}
            />
            <Button
              variant="contained"
              onClick={handleValidateCoupon}
              disabled={loading || !couponCode.trim()}
              fullWidth
              sx={{
                bgcolor: primaryColor, // Usa cor primária personalizada
                color: 'white',
                fontFamily: mergedTheme.typography?.fontFamily || 'Manrope, sans-serif',
                '&:hover': {
                  bgcolor: mergedTheme.colors?.primaryColorHover || primaryColor,
                  opacity: 0.9,
                },
                '&:disabled': {
                  bgcolor: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.26)',
                },
              }}
            >
              {loading ? <CircularProgress size={20} /> : 'Aplicar'}
            </Button>

            {result && !result.success && (
              <Alert severity="error" sx={{ py: 0.5 }}>
                {result.message}
              </Alert>
            )}

            {result && result.success && result.discountAmount && (
              <Alert severity="success" sx={{ py: 0.5 }}>
                <Typography variant="body2" sx={{ fontFamily: 'Manrope, sans-serif' }}>
                  {result.message}
                </Typography>
                <Typography variant="caption" sx={{ fontFamily: 'Manrope, sans-serif' }}>
                  De {formatCurrency(ticketValue)} por {formatCurrency(result.finalValue || 0)}
                  {' '}(economia de {formatCurrency(result.discountAmount)})
                </Typography>
              </Alert>
            )}
          </Box>
        </Collapse>
      )}
    </Box>
  );
}

